import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { useAuthStore } from '../store/auth';
import CopyButton from '../components/CopyButton';

interface CampaignDetail {
  id: string;
  name: string;
  slug: string;
  description?: string;
  terms?: string;
  logoUrl?: string;
  category: string;
  trackingUrl: string;
  previewUrl?: string;
  cookieLifetimeDays: number;
  attributionModel: string;
  currency: string;
  goals: {
    id: string;
    name: string;
    value: string;
    description?: string;
  }[];
  payoutConfigs: {
    id: string;
    title: string;
    paymentType: string;
    amount: number;
    amountType: string;
    currency: string;
    goalValue?: string;
  }[];
  myStats?: {
    clicks: number;
    conversions: number;
    pendingCommissions: number;
    approvedCommissions: number;
  };
}

const paymentTypeLabels: Record<string, string> = {
  cpa: 'CPA',
  cps: 'CPS',
  revshare: 'RevShare',
  hybrid: 'Hybrid',
  cpl: 'CPL',
  cpi: 'CPI',
};

export default function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const { affiliate } = useAuthStore();

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign-detail', id],
    queryFn: async () => {
      const response = await api.get(`/campaigns/${id}/affiliate-view`);
      return response.data.data as CampaignDetail;
    },
  });

  if (isLoading) {
    return <div className="text-center py-10">Loading...</div>;
  }

  if (!campaign) {
    return <div className="text-center py-10">Campaign not found</div>;
  }

  // Build tracking link with affiliate's code
  const trackingLink = campaign.trackingUrl?.replace('{CODE}', affiliate?.referralCode || '') || '';

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link to="/campaigns" className="text-sm text-[var(--text-muted)] hover:text-[var(--text-secondary)]">
          &larr; Back to Campaigns
        </Link>
        <div className="mt-4 flex items-center gap-4">
          {campaign.logoUrl ? (
            <img
              src={campaign.logoUrl}
              alt={campaign.name}
              className="h-16 w-16 rounded-lg object-cover"
            />
          ) : (
            <div className="h-16 w-16 rounded-lg bg-primary-100 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary-600">
                {campaign.name.charAt(0)}
              </span>
            </div>
          )}
          <div>
            <h1 className="text-2xl font-semibold text-[var(--text-primary)]">{campaign.name}</h1>
            <p className="text-sm text-[var(--text-muted)]">{campaign.category}</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      {campaign.myStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Clicks" value={campaign.myStats.clicks} />
          <StatCard label="Conversions" value={campaign.myStats.conversions} />
          <StatCard
            label="Pending"
            value={`${campaign.currency} ${campaign.myStats.pendingCommissions.toFixed(2)}`}
            color="yellow"
          />
          <StatCard
            label="Approved"
            value={`${campaign.currency} ${campaign.myStats.approvedCommissions.toFixed(2)}`}
            color="green"
          />
        </div>
      )}

      {/* Tracking Link */}
      <div className="bg-[var(--bg-secondary)] shadow rounded-lg p-6 mb-6">
        <h3 className="text-lg font-medium mb-4">Your Tracking Link</h3>
        <div className="flex items-center gap-2">
          <input
            type="text"
            readOnly
            value={trackingLink}
            className="input flex-1 bg-[var(--bg-secondary)]"
          />
          <CopyButton text={trackingLink} />
        </div>
        {campaign.previewUrl && (
          <p className="mt-2 text-sm text-[var(--text-muted)]">
            Preview:{' '}
            <a
              href={campaign.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:underline"
            >
              {campaign.previewUrl}
            </a>
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Payouts */}
        <div className="bg-[var(--bg-secondary)] shadow rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">Payout Structure</h3>
          {campaign.payoutConfigs?.length === 0 ? (
            <p className="text-[var(--text-muted)] text-sm">No payout configs available.</p>
          ) : (
            <div className="space-y-3">
              {campaign.payoutConfigs?.map((payout) => (
                <div
                  key={payout.id}
                  className="flex items-center justify-between p-3 bg-[var(--bg-secondary)] rounded-lg"
                >
                  <div>
                    <p className="font-medium text-[var(--text-primary)]">{payout.title}</p>
                    <p className="text-sm text-[var(--text-muted)]">
                      {paymentTypeLabels[payout.paymentType] || payout.paymentType}
                      {payout.goalValue && ` - ${payout.goalValue}`}
                    </p>
                  </div>
                  <span className="text-lg font-semibold text-green-600">
                    {payout.amountType === 'percentage'
                      ? `${payout.amount}%`
                      : `${payout.currency} ${payout.amount}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Goals */}
        <div className="bg-[var(--bg-secondary)] shadow rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">Conversion Goals</h3>
          {campaign.goals?.length === 0 ? (
            <p className="text-[var(--text-muted)] text-sm">No goals defined.</p>
          ) : (
            <div className="space-y-3">
              {campaign.goals?.map((goal) => (
                <div key={goal.id} className="p-3 bg-[var(--bg-secondary)] rounded-lg">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-[var(--text-primary)]">{goal.name}</p>
                    <code className="text-sm bg-gray-200 px-2 py-1 rounded">
                      {goal.value}
                    </code>
                  </div>
                  {goal.description && (
                    <p className="text-sm text-[var(--text-muted)] mt-1">{goal.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Description & Terms */}
      {(campaign.description || campaign.terms) && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {campaign.description && (
            <div className="bg-[var(--bg-secondary)] shadow rounded-lg p-6">
              <h3 className="text-lg font-medium mb-4">Description</h3>
              <p className="text-[var(--text-secondary)] whitespace-pre-wrap">{campaign.description}</p>
            </div>
          )}
          {campaign.terms && (
            <div className="bg-[var(--bg-secondary)] shadow rounded-lg p-6">
              <h3 className="text-lg font-medium mb-4">Terms & Conditions</h3>
              <p className="text-[var(--text-secondary)] whitespace-pre-wrap text-sm">{campaign.terms}</p>
            </div>
          )}
        </div>
      )}

      {/* Campaign Details */}
      <div className="mt-6 bg-[var(--bg-secondary)] shadow rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Campaign Details</h3>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <dt className="text-sm text-[var(--text-muted)]">Cookie Lifetime</dt>
            <dd className="text-[var(--text-primary)] font-medium">{campaign.cookieLifetimeDays} days</dd>
          </div>
          <div>
            <dt className="text-sm text-[var(--text-muted)]">Attribution</dt>
            <dd className="text-[var(--text-primary)] font-medium capitalize">
              {campaign.attributionModel.replace('_', ' ')}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-[var(--text-muted)]">Currency</dt>
            <dd className="text-[var(--text-primary)] font-medium">{campaign.currency}</dd>
          </div>
          <div>
            <dt className="text-sm text-[var(--text-muted)]">Campaign ID</dt>
            <dd className="text-[var(--text-primary)] font-mono text-sm">{campaign.slug}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  color = 'gray',
}: {
  label: string;
  value: string | number;
  color?: 'gray' | 'green' | 'yellow';
}) {
  const colorClasses = {
    gray: 'bg-[var(--bg-tertiary)]',
    green: 'bg-green-50',
    yellow: 'bg-yellow-50',
  };

  return (
    <div className={`rounded-lg p-4 ${colorClasses[color]}`}>
      <p className="text-sm text-[var(--text-muted)]">{label}</p>
      <p className="text-2xl font-semibold text-[var(--text-primary)]">{value}</p>
    </div>
  );
}
